import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	Injectable,
	PLATFORM_ID,
	computed,
	inject,
	signal,
} from '@angular/core';
import { AuthService } from '@app/core/services/auth.service';
import { Observable, catchError, of, tap } from 'rxjs';

/**
 * Estado de la suscripción para la interfaz (decisión 4 del 5-oct: con la
 * suscripción vencida la app de IA se abre en SOLO LECTURA — ver y exportar las
 * sesiones, nada de crearlas ni continuarlas).
 *
 * Tres valores, no dos:
 *  - `true`: activa.
 *  - `false`: vencida — el backend respondió 403 a `check-subscription`.
 *  - `null`: no se sabe (servidor SSR, red caída, 5xx…).
 *
 * `null` NO es «vencida»: si una caída de la API pusiera la interfaz en solo
 * lectura, un profesional al día perdería el chat por un fallo de
 * infraestructura. Quien manda de verdad es el backend, que sigue exigiendo la
 * suscripción en cada escritura; esto solo decide qué se enseña.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionStateService {
	#auth = inject(AuthService);
	#platform = inject(PLATFORM_ID);

	#active = signal<boolean | null>(null);
	readonly active = this.#active.asReadonly();
	readonly readOnly = computed(() => this.#active() === false);

	/** Pregunta al backend y guarda la respuesta. Nunca emite error. */
	refresh(): Observable<boolean | null> {
		// En el servidor (SSR) no hay sesión: el JWT vive en el navegador.
		if (!isPlatformBrowser(this.#platform)) return of(null);

		return this.#auth.checkSubscription().pipe(
			catchError((error: HttpErrorResponse) =>
				of(error?.status === 403 ? false : null)
			),
			tap(active => this.#active.set(active))
		);
	}

	/**
	 * Un 403 `subscription_invalid` de cualquier escritura: la suscripción venció
	 * con la pantalla abierta. Se pasa a solo lectura al momento, sin esperar a la
	 * siguiente navegación.
	 */
	markExpired(): void {
		this.#active.set(false);
	}
}
